import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '../settings';
import {
  Typography, Grid, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, Box, Alert, Paper,
  Checkbox, FormControlLabel, Divider,
  CircularProgress, Tabs, Tab,
  RadioGroup,
  Card,
  Radio,
} from '@mui/material';
import NavigationIcon from '@mui/icons-material/Navigation';
import { useAuth } from '../context/AuthContext';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import ADLSection from './ADLSection';
import { calculateAdlScore } from '../utils/adlUtils';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MyLocationIcon from "@mui/icons-material/MyLocation";

/* ---------- leaflet fix ---------- */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});
/* -------------------------------- */

function MapClickHandler({ onPositionChange, disabled }) {
  useMapEvents({
    click(e) {
      if (!disabled) onPositionChange(e.latlng);
    },
  });
  return null;
}

const emptyHealthBehavior = {
  exercise_freq: '',
  exercise_type: 'None',
  diet_taste: [],
  smoking: 'No',
  smoke_per_day: '',
  alcohol: 'No',
  alcohol_freq: '',
  temperament: [],
  self_treatment: [],
};




/* ---------- ADL ---------- */
const emptyAdl = {
  feeding: '',
  grooming: '',
  transfer: '',
  toilet_use: '',
  mobility: '',
  dressing: '',
  stairs: '',
  bathing: '',
  bowels: '',
  bladder: '',
};

const isAdlCompleted = (adl) =>
  Object.values(adl).every(
    v => v !== '' && v !== null && v !== undefined
  );

/* ------------------------ */

function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const canEdit = user?.role === 'ADMIN' || user?.role === 'OFFICER';

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({});
  const [citizenError, setCitizenError] = useState('');

  const [volunteers, setVolunteers] = useState([]);
  const [formData, setFormData] = useState(null);

  const [adl, setAdl] = useState(emptyAdl);
  const [adlHistory, setAdlHistory] = useState([]);

  const [healthBehavior, setHealthBehavior] = useState(emptyHealthBehavior);
  const [healthBehaviorId, setHealthBehaviorId] = useState(null);


  /* ---------- DEPRESSION ---------- */
  const emptyDepression = {
    answer2q_1: null,
    answer2q_2: null,

    answer9q_1: 0,
    answer9q_2: 0,
    answer9q_3: 0,
    answer9q_4: 0,
    answer9q_5: 0,
    answer9q_6: 0,
    answer9q_7: 0,
    answer9q_8: 0,
    answer9q_9: 0,
  };

  const [depression, setDepression] = useState(emptyDepression);

  const is2QPositive =
    depression.answer2q_1 === true ||
    depression.answer2q_2 === true;


  const emptyStress = {
    answer_st5_1: 0,
    answer_st5_2: 0,
    answer_st5_3: 0,
    answer_st5_4: 0,
    answer_st5_5: 0,
  };
  const [stress, setStress] = useState(emptyStress);


  /* ---------- load ---------- */
  useEffect(() => {
    const loadAll = async () => {
      try {
        const [pRes, vRes, adlRes, hbRes] = await Promise.all([
          fetch(`${API_URL}/patients/${id}`),
          fetch(`${API_URL}/users?role=VOLUNTEER`),
          fetch(`${API_URL}/adl_records?patient_id=${id}&_sort=created_at&_order=desc`),
          // fetch(`${API_URL}/adl_records?patient_id=${String(id)}&_sort=created_at&_order=desc`),
          fetch(`${API_URL}/health_behaviors?patient_id=${id}`)
        ]);


        const patient = await pRes.json();
        const vols = await vRes.json();
        const adls = await adlRes.json();
        const hbs = await hbRes.json();

        setDepression(patient.depression || emptyDepression);
        setStress(patient.stress || emptyStress);

        setVolunteers(vols);
        setAdlHistory(adls);

        if (adls.length > 0) {
          const latest = adls[adls.length - 1];
          setAdl({
            feeding: latest.feeding,
            grooming: latest.grooming,
            transfer: latest.transfer,
            toilet_use: latest.toilet_use,
            mobility: latest.mobility,
            dressing: latest.dressing,
            stairs: latest.stairs,
            bathing: latest.bathing,
            bowels: latest.bowels,
            bladder: latest.bladder,
          });
        } else {
          setAdl(emptyAdl);
        }


        if (hbs.length > 0) {
          setHealthBehavior(hbs[0]);
          setHealthBehaviorId(hbs[0].id);
        }

        setFormData({
          ...patient,

          // address
          house_no: patient.address_detail?.house_no || '',
          village: patient.address_detail?.village || '',
          alley: patient.address_detail?.alley || '',
          road: patient.address_detail?.road || '',
          subdistrict: patient.address_detail?.subdistrict || '',
          district: patient.address_detail?.district || '',
          province: patient.address_detail?.province || '',
          lat: patient.address_detail?.lat || '',
          lng: patient.address_detail?.lng || '',

          // economy
          income_status: patient.economy?.status || '',
          occupation: patient.economy?.occupation || '',
          income_source: patient.economy?.source || '',
          welfare_received: patient.economy?.welfare || false,
          economic_notes: patient.economy?.notes || '',
          
          patient_image: patient.patient_image || '', // Add image field
        });


      } catch (err) {
        setStatus({ type: 'error', message: err.message });
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [id]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!formData) return <Alert severity="error">ไม่พบข้อมูลผู้สูงอายุ</Alert>;

  const mapPosition =
    formData.lat && formData.lng
      ? [Number(formData.lat), Number(formData.lng)]
      : [13.7563, 100.5018];

  /* ---------- handlers ---------- */
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageUpload = (event) => {
    if (!canEdit) return;
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          patient_image: reader.result // Base64 string
        }));
      };
      reader.readAsDataURL(file);
    }
  };


  const handleGetCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition((pos) => {
      setFormData((prev) => ({
        ...prev,
        lat: pos.coords.latitude.toFixed(6),
        lng: pos.coords.longitude.toFixed(6),
      }));
    });
  };


  const handleMapClick = latlng => {
    if (!canEdit) return;
    setFormData(p => ({
      ...p,
      lat: latlng.lat.toFixed(6),
      lng: latlng.lng.toFixed(6),
    }));
  };

  const handleCitizenBlur = async () => {
    if (!formData.citizen_id) return;
    const res = await fetch(`${API_URL}/patients?citizen_id=${formData.citizen_id}`);
    const list = await res.json();
    if (list.length > 0 && list[0].id !== Number(id)) {
      setCitizenError('เลขบัตรประชาชนนี้ถูกใช้แล้ว');
    } else {
      setCitizenError('');
    }
  };

  const handleSave = async e => {
    e.preventDefault();
    if (citizenError) return;

    const {
      lat, lng,
      income_status, occupation, income_source,
      welfare_received, economic_notes,
      ...rest
    } = formData;

    const { score, label } = calculate9QResult(depression);
    const { score: stScore, label: stLabel } = calculateST5Result(stress);

    await fetch(`${API_URL}/patients/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        citizen_id: formData.citizen_id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        gender: formData.gender,
        birth_date: formData.birth_date,
        age: Number(formData.age),
        phone: formData.phone,
        email: formData.email,

        marital_status: formData.marital_status,
        living_alone: formData.living_alone,
        bedridden_level: formData.bedridden_level,
        volunteer_id: formData.volunteer_id,

        care_first_name: formData.care_first_name,
        care_last_name: formData.care_last_name,
        care_related: formData.care_related,


        address_detail: {
          house_no: formData.house_no,
          village: formData.village,
          alley: formData.alley,
          road: formData.road,
          subdistrict: formData.subdistrict,
          district: formData.district,
          province: formData.province,
          lat: Number(formData.lat),
          lng: Number(formData.lng),
        },

        economy: {
          status: formData.income_status,
          occupation: formData.occupation,
          source: formData.income_source,
          welfare: formData.welfare_received,
          notes: formData.economic_notes,
        },

        // Depression save
        depression: {
          answer2q_1: depression.answer2q_1,
          answer2q_2: depression.answer2q_2,
          result_2q: is2QPositive,

          ...(is2QPositive && {
            answer9q_1: depression.answer9q_1,
            answer9q_2: depression.answer9q_2,
            answer9q_3: depression.answer9q_3,
            answer9q_4: depression.answer9q_4,
            answer9q_5: depression.answer9q_5,
            answer9q_6: depression.answer9q_6,
            answer9q_7: depression.answer9q_7,
            answer9q_8: depression.answer9q_8,
            answer9q_9: depression.answer9q_9,
            result_9q_score: score,
            result_9q_label: label,
            assessed_date: new Date().toISOString().split("T")[0],
            assessed_by: formData.volunteer_id || null
          })
        },

        stress: {
          answer_st5_1: stress.answer_st5_1,
          nswer_st5_2: stress.answer_st5_2,
          answer_st5_3: stress.answer_st5_3,
          answer_st5_4: stress.answer_st5_4,
          answer_st5_5: stress.answer_st5_5,
          result_st5_score: stScore,
          result_st5_label: stLabel,
          assessed_date: new Date().toISOString().split("T")[0],
          assessed_by: formData.volunteer_id || null
        },
        
        patient_image: formData.patient_image || null,

        updated_at: new Date().toISOString(),
      })
    });


    setStatus({ type: 'success', message: 'บันทึกข้อมูลเรียบร้อย' });
  };

  const toggleArrayValue = (field, value) => {
    setHealthBehavior(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  /* ---------- ADL save ---------- */
  const handleSaveAdl = async () => {
    if (!isAdlCompleted(adl)) {
      alert('กรุณาประเมิน ADL ให้ครบทุกข้อ');
      return;
    }

    const { total, group } = calculateAdlScore(adl);

    await fetch(`${API_URL}/adl_records`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        patient_id: String(id),
        assessment_date: new Date().toISOString().split('T')[0],
        ...adl,
        total_score: total,
        adl_group: group,
        assessed_by: formData.volunteer_id || null,
        created_at: new Date().toISOString(),
      })
    });

    await fetch(`${API_URL}/patients/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        adl_summary: {
          latest_score: total,
          latest_group: group,
          last_assessed: new Date().toISOString().split('T')[0],
        }
      })
    });

    alert('บันทึก ADL ใหม่เรียบร้อย');
    window.location.reload();
  };

  const calculate9QResult = (dep) => {
    const score = [
      dep.answer9q_1,
      dep.answer9q_2,
      dep.answer9q_3,
      dep.answer9q_4,
      dep.answer9q_5,
      dep.answer9q_6,
      dep.answer9q_7,
      dep.answer9q_8,
      dep.answer9q_9,
    ].reduce((s, v) => s + Number(v || 0), 0);

    let label = "ไม่มีอาการของโรคซึมเศร้า";
    if (score >= 7 && score <= 12)
      label = "มีอาการของโรคซึมเศร้า ระดับน้อย";
    else if (score >= 13 && score <= 18)
      label = "มีอาการของโรคซึมเศร้า ระดับปานกลาง";
    else if (score >= 19)
      label = "มีอาการของโรคซึมเศร้า ระดับรุนแรง";

    return { score, label };
  };


  const calculateST5Result = (st) => {
    const score = [
      st.answer_st5_1,
      st.answer_st5_2,
      st.answer_st5_3,
      st.answer_st5_4,
      st.answer_st5_5,
    ].reduce((s, v) => s + Number(v || 0), 0);

    let label = "ความเครียดน้อย";
    if (score >= 5 && score <= 7) label = "ความเครียดปานกลาง";
    else if (score >= 8 && score <= 9) label = "ความเครียดมาก";
    else if (score >= 10) label = "ความเครียดมากที่สุด";

    return { score, label };
  };



  const handleNavigate = () => {
    if (formData.lat && formData.lng) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${formData.lat},${formData.lng}`,
        '_blank'
      );
    }
  };

  /* ---------- render ---------- */
  return (
    <>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
        ข้อมูลผู้สูงอายุ
      </Typography>

      <Paper sx={{ p: 4 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
          <Tab label="ข้อมูลทั่วไป" />          {/* 0 */}
          <Tab label="ที่อยู่ / การอยู่อาศัย" />          {/* 1 */}
          <Tab label="สุขภาพพื้นฐาน" />        {/* 2 */}
          <Tab label="พฤติกรรมสุขภาพ" />       {/* 3 */}
          <Tab label="เศรษฐกิจ" />              {/* 4 */}
          <Tab label="แบบประเมินภาวะซึมเศร้า" />              {/* 5 */}
          <Tab label="แบบประเมินภาวะเครียด" />              {/* 6 */}
          <Tab label="แบบประเมิน ADL" />                   {/* 7 */}
        </Tabs>

        {/* ================= TAB 0 : ประวัติ ================= */}
        {tab === 0 && (
          <>
            <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 12 }}>
                {formData.patient_image ? (
                  <Box sx={{ mb: 2, maxWidth: 200 }}>
                    <img
                      src={formData.patient_image}
                      alt="Patient Profile"
                      style={{ width: '100%', height: 'auto', borderRadius: 8 }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ mb: 2, width: 150, height: 150, backgroundColor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
                    <Typography color="text.secondary">ไม่มีรูปภาพ</Typography>
                  </Box>
                )}
                {canEdit && (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CameraAltIcon />}
                  >
                    ถ่ายภาพ/เปลี่ยนรูป
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      hidden
                      onChange={handleImageUpload}
                    />
                  </Button>
                )}
                {canEdit && formData.patient_image && (
                  <Button size="small" color="error" onClick={() => setFormData(prev => ({ ...prev, patient_image: "" }))} sx={{ ml: 1 }}>
                    ลบรูป
                  </Button>
                )}
              </Grid>
            </Grid>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="เลขบัตรประชาชน"
                  name="citizen_id"
                  value={formData.citizen_id || ''}
                  onChange={handleChange}
                  onBlur={handleCitizenBlur}
                  error={!!citizenError}
                  helperText={citizenError}
                  InputProps={{ readOnly: !!formData.citizen_id || !canEdit }}
                  disabled={!canEdit}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="ชื่อ" name="first_name"
                  value={formData.first_name || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }}
                  disabled={!canEdit}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="นามสกุล" name="last_name"
                  value={formData.last_name || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }}
                  disabled={!canEdit}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="วันเกิด" name="birth_date" type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.birth_date || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }}
                  disabled={!canEdit}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="อายุ" name="age" type="number"
                  value={formData.age || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }}
                  disabled={!canEdit}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="เบอร์โทร" name="phone"
                  value={formData.phone || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }}
                  disabled={!canEdit}
                />
              </Grid>

              {/* <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="อีเมล" name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }}
                  disabled={!canEdit}

                />
              </Grid> */}

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>เพศ</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender || ''}
                    onChange={handleChange}
                    disabled={!canEdit}
                  >
                    <MenuItem value="M">ชาย</MenuItem>
                    <MenuItem value="F">หญิง</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>สถานภาพสมรส</InputLabel>
                  <Select
                    name="marital_status"
                    value={formData.marital_status || ''}
                    onChange={handleChange}
                    disabled={!canEdit}
                  >
                    <MenuItem value="โสด">โสด</MenuItem>
                    <MenuItem value="สมรส">สมรส</MenuItem>
                    <MenuItem value="หม้าย">หม้าย</MenuItem>
                    <MenuItem value="หย่าร้าง">หย่าร้าง</MenuItem>
                  </Select>
                </FormControl>
              </Grid> */}

              {/* <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="HN (เลขโรงพยาบาล)"
                  name="hn"
                  value={formData.hn || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }}
                  disabled={!canEdit}
                />
              </Grid> */}
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>เจ้าหน้าที่ อสม.</InputLabel>
                  <Select
                    value={formData.volunteer_id ?? ""}
                    label="อาสาสมัคร"
                    onChange={handleChange}
                    name="volunteer_id"
                  >
                    <MenuItem value="">
                      <em>ยังไม่กำหนด</em>
                    </MenuItem>
                    {volunteers.map(v => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="รพ.สต."
                  name="health_station"
                  value={formData.health_station}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

          </>
        )}

        {/* ================= TAB 1 : ที่อยู่อาศัย ================= */}
        {tab === 1 && (
          <>
            {/* ================= ที่อยู่อาศัย ================= */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="บ้านเลขที่"
                  name="house_no"
                  value={formData.house_no || ""}
                  onChange={handleChange}
                  disabled={!canEdit}

                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="หมู่"
                  name="village"
                  value={formData.village || ""}
                  onChange={handleChange}
                  disabled={!canEdit}

                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="ซอย"
                  name="alley"
                  value={formData.alley || ""}
                  onChange={handleChange}
                  disabled={!canEdit}

                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="ถนน"
                  name="road"
                  value={formData.road || ""}
                  onChange={handleChange}
                  disabled={!canEdit}

                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="ตำบล / แขวง"
                  name="subdistrict"
                  value={formData.subdistrict || ""}
                  onChange={handleChange} disabled={!canEdit}

                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="อำเภอ / เขต"
                  name="district"
                  value={formData.district || ""}
                  onChange={handleChange}
                  disabled={!canEdit}

                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="จังหวัด"
                  name="province"
                  value={formData.province || ""}
                  onChange={handleChange}
                  disabled={!canEdit}

                />
              </Grid>

              {/* สถานะการอยู่อาศัย / สังคม */}
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="living_alone"
                      checked={formData.living_alone}
                      onChange={handleChange}
                      disabled={!canEdit}

                    />
                  }
                  label="อาศัยอยู่คนเดียว"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 12 }}>
                <Typography variant="p" sx={{ mb: 2, fontWeight: 'bold' }}>
                  ข้อมูลผู้ดูแล
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="ชื่อ"
                  name="care_first_name"
                  value={formData.care_first_name || ""}
                  onChange={handleChange}
                  disabled={!canEdit}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="นามสกุล"
                  name="care_last_name"
                  value={formData.care_last_name || ""}
                  onChange={handleChange}
                  disabled={!canEdit}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="ความสัมพันธ์"
                  name="care_related"
                  value={formData.care_related || ""}
                  onChange={handleChange}
                  disabled={!canEdit}
                />
              </Grid>

              {/* <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth sx={{ '& .MuiInputBase-root': { height: 56 } }}>
                  <InputLabel>ระดับผู้ป่วยติดเตียง</InputLabel>
                  <Select
                    name="bedridden_level"
                    value={formData.bedridden_level}
                    onChange={handleChange}
                    disabled={!canEdit}
                  >
                    <MenuItem value="Independent">ไม่ติดเตียง</MenuItem>
                    <MenuItem value="Homebound">ติดบ้าน</MenuItem>
                    <MenuItem value="Bedridden">ติดเตียง</MenuItem>
                  </Select>
                </FormControl>
              </Grid> */}
            </Grid>
            {/* ================= MAP ================= */}
            <Grid size={{ xs: 12, md: 12 }} sx={{ mt: 2 }}>
              <Typography variant="p" sx={{ mb: 2, fontWeight: 'bold' }}>
                ที่ตั้งในแผนที่
              </Typography>
            </Grid>

            <Box sx={{ height: 350, mt: 2 }}>
              <MapContainer
                key={`${formData.lat}-${formData.lng}`}
                center={mapPosition}
                zoom={formData.lat ? 20 : 15}
                style={{ height: '100%' }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <MapClickHandler onPositionChange={handleMapClick} disabled={!canEdit} />
                {formData.lat && <Marker position={mapPosition} />}
              </MapContainer>
            </Box>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* 🔹 ปุ่มตำแหน่งปัจจุบัน */}
              <Grid size={{ xs: 12, md: 12, mt: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<MyLocationIcon />}
                  onClick={handleGetCurrentLocation}
                  disabled={!canEdit}
                >
                  ใช้ตำแหน่งปัจจุบัน
                </Button>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Latitude" value={formData.lat} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth label="Longitude" value={formData.lng} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid size={{ xs: 12, md: 12 }}>
                <Button fullWidth variant="outlined" startIcon={<NavigationIcon />}
                  onClick={handleNavigate} disabled={!formData.lat}>
                  นำทาง
                </Button>
              </Grid>
            </Grid>
          </>
        )}


        {/* ================= TAB 2 : สุขภาพพื้นฐาน ================= */}
        {tab === 2 && (
          <>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>กรุ๊ปเลือด</InputLabel>
                  <Select name="blood_type" value={formData.blood_type || ''}
                    onChange={handleChange} disabled={!canEdit}>
                    <MenuItem value="A">A</MenuItem>
                    <MenuItem value="B">B</MenuItem>
                    <MenuItem value="AB">AB</MenuItem>
                    <MenuItem value="O">O</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Rh</InputLabel>
                  <Select name="rh_factor" value={formData.rh_factor || ''}
                    onChange={handleChange} disabled={!canEdit}>
                    <MenuItem value="+">Rh+</MenuItem>
                    <MenuItem value="-">Rh-</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="ส่วนสูง (ซม.)" name="height_cm"
                  value={formData.height_cm || ''} onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="น้ำหนัก (กก.)" name="weight_kg"
                  value={formData.weight_kg || ''} onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField fullWidth multiline rows={2} label="ประวัติแพ้ยา / อาหาร"
                  name="allergy" value={formData.allergy || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>


              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="ประวัติโรคในครอบครัว"
                  name="family_disease_history"
                  value={formData.family_disease_history || ""}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="อาการปัจจุบัน"
                  name="symptom"
                  value={formData.symptom || ""}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>


              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="ความพิการ"
                  name="disability"
                  value={formData.disability || ""}
                  onChange={handleChange}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="โรคประจำตัว"
                  name="chronic_disease_note"
                  value={formData.chronic_disease_note || ""}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="ยาที่รับประทานประจำ"
                  name="regular_medications"
                  value={formData.regular_medications || ""}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="สถานพยาบาลที่รักษา"
                  name="primary_care_facility"
                  value={formData.primary_care_facility || ""}
                  onChange={handleChange}

                  InputProps={{ readOnly: !canEdit }} />
              </Grid>



              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="การได้รับผ้าอ้อม"
                  name="social_sup"
                  value={formData.social_sup || ""}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>

            </Grid>
          </>
        )}

        {/* ================= TAB 3 : พฤติกรรมสุขภาพ ================= */}
        {tab === 3 && (
          <>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              พฤติกรรมสุขภาพ
            </Typography>

            <Grid container spacing={3}>

              {/* การออกกำลังกาย */}
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="ออกกำลังกาย (ครั้ง/สัปดาห์)"
                  type="number"
                  value={healthBehavior.exercise_freq}
                  onChange={(e) =>
                    setHealthBehavior(prev => ({
                      ...prev,
                      exercise_freq: e.target.value
                    }))
                  }

                  InputProps={{ readOnly: !canEdit }} />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>การออกกำลังกาย</InputLabel>
                  <Select
                    value={healthBehavior.exercise_type}
                    onChange={(e) =>
                      setHealthBehavior(prev => ({
                        ...prev,
                        exercise_type: e.target.value
                      }))
                    }
                    InputProps={{ readOnly: !canEdit }} >
                    <MenuItem value="Exercise">ออกกำลังกาย</MenuItem>
                    <MenuItem value="None">ไม่ออกกำลังกาย</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* รสนิยมอาหาร */}
              <Grid size={{ xs: 12 }}>
                <Typography fontWeight="bold">รสนิยมอาหาร</Typography>
                {["Salty : เค็ม", "Sweet : หวาน", "Sour : เปรี้ยว", "Spicy : เผ็ด", "Bland : จืด"].map(v => (
                  <FormControlLabel
                    key={v}
                    control={
                      <Checkbox
                        checked={healthBehavior.diet_taste.includes(v)}
                        onChange={() => toggleArrayValue("diet_taste", v)}
                        InputProps={{ readOnly: !canEdit }} />
                    }
                    label={v}
                  />
                ))}
              </Grid>

              {/* บุหรี่ */}
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>การสูบบุหรี่</InputLabel>
                  <Select
                    value={healthBehavior.smoking}
                    onChange={(e) =>
                      setHealthBehavior(prev => ({
                        ...prev,
                        smoking: e.target.value
                      }))
                    }
                    InputProps={{ readOnly: !canEdit }}>
                    <MenuItem value="No">ไม่สูบ</MenuItem>
                    <MenuItem value="Yes">สูบ</MenuItem>
                    <MenuItem value="Quit">เลิกแล้ว</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {healthBehavior.smoking === "Yes" && (
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="จำนวนมวน/วัน"
                    type="number"
                    value={healthBehavior.smoke_per_day}
                    onChange={(e) =>
                      setHealthBehavior(prev => ({
                        ...prev,
                        smoke_per_day: e.target.value
                      }))
                    }
                    InputProps={{ readOnly: !canEdit }} />
                </Grid>
              )}

              {/* แอลกอฮอล์ */}
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>การดื่มสุรา</InputLabel>
                  <Select
                    value={healthBehavior.alcohol}
                    onChange={(e) =>
                      setHealthBehavior(prev => ({
                        ...prev,
                        alcohol: e.target.value
                      }))
                    }
                    InputProps={{ readOnly: !canEdit }}>
                    <MenuItem value="No">ไม่ดื่ม</MenuItem>
                    <MenuItem value="Occasional">ดื่มบางครั้ง</MenuItem>
                    <MenuItem value="Yes">ดื่มประจำ</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {healthBehavior.alcohol !== "No" && (
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="จำนวนครั้ง/สัปดาห์"
                    type="number"
                    value={healthBehavior.alcohol_freq}
                    onChange={(e) =>
                      setHealthBehavior(prev => ({
                        ...prev,
                        alcohol_freq: e.target.value
                      }))
                    }
                    InputProps={{ readOnly: !canEdit }} />
                </Grid>
              )}

              {/* อุปนิสัย */}
              <Grid size={{ xs: 12 }}>
                <Typography fontWeight="bold">อุปนิสัย</Typography>
                  {["Calm : ใจเย็น", "Irritable : หงุดหงิดง่าย", "Cheerful : ร่าเริง", "Stressful : เครียดง่าย"].map(v => (
                  <FormControlLabel
                    key={v}
                    control={
                      <Checkbox
                        checked={healthBehavior.temperament.includes(v)}
                        onChange={() => toggleArrayValue("temperament", v)}
                      />
                    }
                    label={v}
                    InputProps={{ readOnly: !canEdit }} />
                ))}
              </Grid>

              {/* การดูแลตนเอง */}
              <Grid size={{ xs: 12 }}>
                <Typography fontWeight="bold">การดูแลตนเองเมื่อเจ็บป่วย</Typography>
                  {["BuyMedicine : ซื้อยากินเอง", "LocalHealer : ภูมิปัญญาพื้นบ้าน", "HealthCenter : รพ.สต/ศูนย์บริการสาธารณสุข", "Hospital : โรงพยาบาล"].map(v => (
                  <FormControlLabel
                    key={v}
                    control={
                      <Checkbox
                        checked={healthBehavior.self_treatment.includes(v)}
                        onChange={() => toggleArrayValue("self_treatment", v)}
                      />
                    }
                    label={v}
                    InputProps={{ readOnly: !canEdit }} />
                ))}
              </Grid>


              {/* การดูแลตนเอง */}
              {/* <Grid size={{ xs: 12 }}>
                <Typography fontWeight="bold">การดูแลตนเองเมื่อเจ็บป่วย</Typography>
                {["BuyMedicine", "LocalHealer", "HealthCenter", "Hospital"].map(v => (
                  <FormControlLabel
                    key={v}
                    control={
                      <Checkbox
                        checked={healthBehavior.self_treatment.includes(v)}
                        onChange={() => toggleArrayValue("self_treatment", v)}
                      />
                    }
                    label={v}
                    InputProps={{ readOnly: !canEdit }} />
                ))}
              </Grid> */}


            </Grid>
          </>
        )}

        {/* ================= TAB 4 : เศรษฐกิจ ================= */}
        {tab === 4 && (
          <>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>สถานะรายได้</InputLabel>
                  <Select name="income_status" value={formData.income_status || ''}
                    onChange={handleChange} disabled={!canEdit}>
                    <MenuItem value="มี">มี</MenuItem>
                    <MenuItem value="รายได้น้อย">รายได้น้อย</MenuItem>
                    <MenuItem value="ไม่มี">ไม่มี</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="อาชีพ" name="occupation"
                  value={formData.occupation || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth label="แหล่งรายได้หลัก" name="income_source"
                  value={formData.income_source || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="welfare_received"
                      checked={Boolean(formData.welfare_received)}
                      onChange={handleChange}
                      disabled={!canEdit}
                    />
                  }
                  label="ได้รับสวัสดิการจากรัฐ"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField fullWidth multiline rows={3}
                  label="บันทึกเพิ่มเติม (ด้านเศรษฐกิจ)"
                  name="economic_notes"
                  value={formData.economic_notes || ''}
                  onChange={handleChange}
                  InputProps={{ readOnly: !canEdit }} />
              </Grid>
            </Grid>
          </>
        )}

        {tab === 5 && (
          <>
            <Typography fontWeight="bold">แบบคัดกรองโรคซึมเศร้า </Typography>
            <Divider sx={{ my: 2 }} />
            {/* ================= 2Q ================= */}
            {[
              { key: "answer2q_1", text: "ใน 2 สัปดาห์ที่ผ่านมา รู้สึกเศร้า ท้อแท้ หรือไม่" },
              { key: "answer2q_2", text: "ใน 2 สัปดาห์ที่ผ่านมา เบื่อ ไม่อยากทำอะไร หรือไม่" }
            ].map(q => (
              <Box key={q.key} sx={{ mt: 2 }}>
                <Card
                  key={q.key}
                  sx={{
                    p: 2,
                    my: 2,
                    opacity: 1
                  }}
                >
                  <Typography>{q.text}</Typography>

                  <RadioGroup
                    row
                    value={depression[q.key]}
                    onChange={(e) =>
                      setDepression(prev => ({
                        ...prev,
                        [q.key]: e.target.value === "true"
                      }))
                    }
                  >
                    <FormControlLabel value="true" control={<Radio />} label="มี" />
                    <FormControlLabel value="false" control={<Radio />} label="ไม่มี" />
                  </RadioGroup>
                </Card>
              </Box>
            ))}

            <Typography sx={{ mt: 2 }} fontWeight="bold">แบบประเมินโรคซึมเศร้า </Typography>
            <Divider sx={{ my: 2 }} />

            {is2QPositive && (
              <>
                {[
                  "เบื่อ ไม่สนใจอยากทำอะไร",
                  "ไม่สบายใจ ซึมเศร้า ท้อแท้",
                  "หลับยากหรือหลับมากไป",
                  "เหนื่อยง่าย หรือไม่ค่อยมีแรง",
                  "เบื่ออาหารหรือกินมากไป",
                  "รู้สึกไม่ดีกับตนเอง",
                  "สมาธิไม่ดี",
                  "พูดช้าหรือกระสับกระส่าย",
                  "คิดทำร้ายตนเอง"
                ].map((label, idx) => {
                  const key = `answer9q_${idx + 1}`;
                  return (
                    <Box key={key} sx={{ mt: 2 }}>
                      <Card
                        key={key}
                        sx={{
                          p: 2,
                          my: 2,
                          opacity: 1
                        }}
                      >
                        <Typography fontWeight="bold">{label}</Typography>

                        <RadioGroup
                          row
                          value={depression[key]}
                          onChange={(e) =>
                            setDepression(prev => ({
                              ...prev,
                              [key]: Number(e.target.value)
                            }))
                          }
                        >
                          <FormControlLabel value={0} control={<Radio />} label="ไม่มี (0)" />
                          <FormControlLabel value={1} control={<Radio />} label="บางวัน (1)" />
                          <FormControlLabel value={2} control={<Radio />} label="บ่อย >7 วัน (2)" />
                          <FormControlLabel value={3} control={<Radio />} label="ทุกวัน (3)" />
                        </RadioGroup>
                      </Card>
                    </Box>
                  );
                })}

                {/* ===== Summary แบบ ADL ===== */}
                {(() => {
                  const { score, label } = calculate9QResult(depression);
                  return (
                    <Alert severity="info" sx={{ mt: 3 }}>
                      คะแนนรวม {score} คะแนน — <strong>{label}</strong>
                    </Alert>
                  );
                })()}
              </>
            )}
          </>
        )}

        {tab === 6 && (
          <>
            <Typography variant="h6" fontWeight="bold" sx={{ mt: 3 }}>
              แบบประเมินความเครียด (ST-5)
            </Typography>
            <Divider sx={{ my: 2 }} />

            {[
              "มีปัญหาการนอน นอนไม่หลับหรือนอนมาก",
              "มีสมาธิน้อยลง",
              "หงุดหงิด / กระวนกระวาย / ว้าวุ่นใจ",
              "รู้สึกเบื่อ เซ็ง",
              "ไม่อยากพบปะผู้คน"
            ].map((label, idx) => {
              const key = `answer_st5_${idx + 1}`;
              return (
                <Card key={key} sx={{ p: 2, my: 2 }}>
                  <Typography fontWeight="bold">{idx + 1}. {label}</Typography>
                  <RadioGroup
                    row
                    value={stress[key]}
                    onChange={(e) =>
                      setStress(prev => ({
                        ...prev,
                        [key]: Number(e.target.value)
                      }))
                    }
                  >
                    <FormControlLabel value={0} control={<Radio />} label="แทบไม่มี (0)" />
                    <FormControlLabel value={1} control={<Radio />} label="บางครั้ง (1)" />
                    <FormControlLabel value={2} control={<Radio />} label="บ่อยครั้ง (2)" />
                    <FormControlLabel value={3} control={<Radio />} label="เป็นประจำ (3)" />
                  </RadioGroup>
                </Card>
              );
            })}

            {/* ===== Summary ===== */}
            {(() => {
              const { score, label } = calculateST5Result(stress);
              return (
                <Alert severity="info" sx={{ mt: 2 }}>
                  คะแนนรวม {score} คะแนน — <strong>{label}</strong>
                </Alert>
              );
            })()}

          </>
        )}

        {/* ================= TAB 7 : ADL ================= */}
        {tab === 7 && (
          <>
            <ADLSection adl={adl} setAdl={setAdl} readOnly={!canEdit} />
            {canEdit && (
              <Box sx={{ textAlign: 'right', mt: 2 }}>
                <Button variant="contained" onClick={handleSaveAdl}>
                  บันทึก ADL ใหม่
                </Button>
              </Box>
            )}
          </>
        )}


        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => navigate('/patients')}>กลับ</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleSave}>
              บันทึกข้อมูล
            </Button>
          )}
        </Box>

        {status.message && (
          <Alert severity={status.type} sx={{ mt: 2 }}>
            {status.message}
          </Alert>
        )}
      </Paper>
    </>
  );
}

export default PatientDetail;

